import { isNull, page, sql } from "@apunte-digital/powersync";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SOFT-DELETE — CENTRALIZED LIVE LAYER (single choke point)               │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * This module is the ONLY place where the invariant "live row = `deleted_at IS
 * NULL`" is expressed for this repo's tables with a trash can (`page`). Every
 * live read composes these predicates (the `pages/queries.ts` builders, the
 * ⌘K search, and any future read). The ONLY reads that see deleted
 * rows are the trash (`getTrashedPagesQuery`), the structural
 * tree walks in `pages/mutations.ts` (subtree cohorts must see the
 * WHOLE tree including trash), and the id-scoped `getPageQuery` lookup (the route
 * decides what to show), all three declared in the guard whitelist
 * (`guard-manifest.ts`).
 *
 * Why centralize? The scattered invariant was campaign bug family #1
 * (stats/counters/⌘K/PATCH ignoring `deleted_at`). With one choke
 * point + anti-regression guard, adding a raw `page` read outside
 * the whitelist BREAKS the build (see `live/__tests__/live-access-guard.test.ts`).
 *
 * Schema note (apunte-digital): `page` is the ONLY table with a trash can,
 * self-referenced as a tree through `parent_page_id`. `page_version` and `block`
 * have NO `deleted_at` of their own; they hang off `page` through the `ON DELETE cascade`
 * FK (they die physically when the page is permanently deleted; see
 * `db/trash.rs`) and remain visible/editable while the page is live.
 *
 * Parent-child cohort: `movePageToTrashMutation` (pages/mutations.ts) is a
 * WRITE-TIME CASCADE — moving a page to trash sets the SAME
 * `deleted_at` on its WHOLE still-live subtree (recursively, not just direct
 * children). In other words, in the normal system state, if a page is
 * live (`deleted_at IS NULL`), NONE of its ancestors is in the trash
 * (`restorePageFromTrashMutation` reaffirms this: if the parent is still deleted when
 * restoring, the restored page becomes a root instead of remaining orphaned
 * under a deleted parent). Thus the page's own `deleted_at IS NULL` is enough
 * for the normal case — the NOT EXISTS check below is DEPTH DEFENSE (one
 * level, not recursive) in case a future path breaks that write invariant,
 * not the primary mechanism.
 */

/**
 * "Live page" predicates: the page is not in the trash AND (one-level depth
 * defense) neither is its direct parent. It applies to EVERY
 * live view/query of `page`.
 */
export function livePageFilters() {
  return [isNull(page.deleted_at), parentPageNotTrashed(page.parent_page_id)];
}

/**
 * One-level NOT EXISTS: `parent_page_id` (if present) does not reference a
 * page with non-null `deleted_at`. A root page (`parent_page_id IS NULL`)
 * passes trivially. The `parent_page` alias avoids self-reference ambiguity
 * (the outer table and subquery share the name `page`).
 */
function parentPageNotTrashed(parentPageId: unknown) {
  return sql`(${parentPageId} IS NULL OR NOT EXISTS (
    SELECT 1 FROM ${page} AS parent_page
    WHERE parent_page.id = ${parentPageId} AND parent_page.deleted_at IS NOT NULL
  ))`;
}
